import 'react-datepicker/dist/react-datepicker.css';
import '@library/components/datePicker/ReactDatePickerOverrides.css';
import {
  DateDisplay,
  DatePickerCalendarIcon,
  DatePickerCustomInput,
  DatePickerPrevNextButtons
} from '@library/components/datePicker';
import { CustomDateHeader } from '@library/components/datePicker/CustomDateHeader';
import DatePicker from 'react-datepicker';
import { DatePickerButton } from '@library/components/buttons';
import type { DateRange } from '@library/types/dates';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import { classNames } from '@library/utils';
import { format } from 'date-fns';

export interface PrizepicksDateRangeProps {
  customEndInput?: ReactNode;
  customStartInput?: ReactNode;
  dateFormat?: string;
  disabled?: boolean;
  endDate?: Date;
  endOpen: boolean;
  endDatePlaceholder?: string;
  handleChange: (value: DateRange) => void;
  maxDate?: Date;
  minDate?: Date;
  monthsShown?: number;
  setStartOpen?: (isOpen: boolean) => void;
  setEndOpen?: (isOpen: boolean) => void;
  startDate?: Date;
  startDatePlaceholder?: string;
  startOpen: boolean;
}

export const PrizepicksDateRange = ({
  customEndInput = undefined,
  customStartInput = undefined,
  dateFormat = 'EEE, MMM d',
  disabled = false,
  endDate = undefined,
  endDatePlaceholder = '',
  endOpen,
  handleChange,
  maxDate = undefined,
  minDate = undefined,
  monthsShown = 2,
  setStartOpen = () => {},
  setEndOpen = () => {},
  startDate = undefined,
  startDatePlaceholder = '',
  startOpen
}: PrizepicksDateRangeProps) => {
  const handleClickOutside = () => {
    if (startDate && !endDate) {
      handleChange({
        fromDate: DateTime.fromJSDate(startDate),
        toDate: DateTime.fromJSDate(startDate)
      });
    }

    if (startOpen) {
      setStartOpen(false);
    }

    if (endOpen) {
      setEndOpen(false);
    }
  };

  const handleChangeStartDate = (value: Date) => {
    handleChange({
      fromDate: DateTime.fromJSDate(value),
      toDate: DateTime.fromJSDate(endDate || value)
    });
  };

  const handleChangeEndDate = (value: Date) => {
    handleChange({
      fromDate: DateTime.fromJSDate(startDate || value),
      toDate: DateTime.fromJSDate(value)
    });
  };

  const renderHeader = (headerInfo: {
    customHeaderCount: number;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    monthDate: Date;
  }) => {
    const { customHeaderCount, decreaseMonth, increaseMonth, monthDate } =
      headerInfo;

    return (
      <CustomDateHeader
        monthDate={monthDate}
        oneMonthOnly={monthsShown === 1}
        customHeaderCount={customHeaderCount}
        decreaseMonth={decreaseMonth}
        increaseMonth={increaseMonth}
      />
    );
  };

  const defaultDateRangeStartDisplay = (
    <DatePickerCustomInput
      className={classNames(
        startOpen ? 'bg-soFresh-120' : '',
        disabled ? '' : 'hover:bg-soFresh-120',
        'grid grid-cols-[1fr_28px_28px]'
      )}
    >
      <DatePickerButton className="h-11" disabled={disabled}>
        <DateDisplay className="grid items-center pl-4 sm:grid-cols-[28px_1fr] sm:justify-end">
          <DatePickerCalendarIcon className="hidden sm:grid" />
          <span className="font-heebo px-2 text-left text-sm leading-5">
            {startDate ? format(startDate, dateFormat) : startDatePlaceholder}
          </span>
        </DateDisplay>
      </DatePickerButton>
      <DatePickerPrevNextButtons
        disabled={disabled}
        date={startDate}
        handleChange={handleChangeStartDate}
        maxDate={maxDate}
        minDate={minDate}
        testIdPrefix="start-date-picker-"
      />
    </DatePickerCustomInput>
  );

  const defaultDateRangeEndDisplay = (
    <DatePickerCustomInput
      className={classNames(
        endOpen ? 'bg-soFresh-120' : '',
        disabled ? '' : 'hover:bg-soFresh-120',
        'grid grid-cols-[1fr_28px_28px]'
      )}
    >
      <DatePickerButton className="h-11" disabled={disabled}>
        <DateDisplay className="grid items-center pl-4 sm:grid-cols-[28px_1fr] sm:justify-end">
          <DatePickerCalendarIcon className="hidden sm:grid" />
          <span className="font-heebo px-2 text-left text-sm leading-5">
            {endDate ? format(endDate, dateFormat) : endDatePlaceholder}
          </span>
        </DateDisplay>
      </DatePickerButton>
      <DatePickerPrevNextButtons
        disabled={disabled}
        date={endDate}
        handleChange={handleChangeEndDate}
        maxDate={maxDate}
        minDate={minDate}
        testIdPrefix="end-date-picker-"
      />
    </DatePickerCustomInput>
  );

  return (
    <div
      className={classNames(
        'border-soFresh-140 text-soClean-140 grid h-11 w-full items-center justify-center overflow-hidden rounded-md border',
        startOpen ? 'grid-cols-[1fr_0px_1px_1fr]' : '',
        endOpen ? 'grid-cols-[1fr_1px_1fr_0px]' : '',
        disabled ? 'bg-soFresh-130' : 'bg-soFresh-110',
        !startOpen && !endOpen ? 'grid-cols-[1fr_1px_1fr]' : ''
      )}
      data-testid="daterange-container"
    >
      <DatePicker
        calendarClassName="!bg-soFresh-110 !border-soFresh-140 !text-soClean-140"
        customInput={customStartInput ?? defaultDateRangeStartDisplay}
        dateFormat={dateFormat}
        disabled={disabled}
        endDate={endDate}
        maxDate={maxDate}
        minDate={minDate}
        monthsShown={monthsShown}
        onInputClick={() => setStartOpen(true)}
        onClickOutside={handleClickOutside}
        onChange={(value: Date) => {
          setStartOpen(false);
          handleChangeStartDate(value);
        }}
        open={startOpen}
        renderCustomHeader={renderHeader}
        selected={startDate}
        selectsStart
        showPopperArrow={false}
        startDate={startDate}
        timeClassName={() => '!bg-soFresh-110 !border-soFresh-140'}
      />
      <div className="bg-soFresh-140 h-7 w-px self-center rounded-md" />
      <DatePicker
        calendarClassName="!bg-soFresh-110 !border-soFresh-140 !text-soClean-140"
        customInput={customEndInput ?? defaultDateRangeEndDisplay}
        dateFormat={dateFormat}
        disabled={disabled}
        endDate={endDate}
        maxDate={maxDate}
        minDate={startDate ?? minDate}
        monthsShown={monthsShown}
        onInputClick={() => setEndOpen(true)}
        onClickOutside={handleClickOutside}
        onChange={(value: Date) => {
          handleChangeEndDate(value);
          setEndOpen(false);
        }}
        open={endOpen}
        renderCustomHeader={renderHeader}
        selected={endDate}
        selectsEnd
        showPopperArrow={false}
        startDate={startDate}
        timeClassName={() => '!bg-soFresh-110 !border-soFresh-140'}
      />
    </div>
  );
};
